/**
 * DocsView — Google Docs-style лист всех документов с поиском + фильтрами.
 * Фильтры: отдел, чат, автор, диапазон дат, режим «недавно отредактированные».
 * Клик по карточке → FilePreviewModal (view/edit).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Icon } from "@/components/prototype/Icon";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { Avatar } from "@/components/prototype/Avatar";
import { SaveToDriveModal } from "@/components/SaveToDriveModal";
import { fetchChatTopics, fetchActiveUsers } from "@/api/dashboard";
import {
  pinDoc, unpinDoc, pinDocForAll, unpinDocForAll, deleteDoc, renameAttachment,
  fetchDeletePermission, setDeletePermission,
  type DeletePermission,
} from "@/api/drive";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";

interface DocItem {
  attachment_id: number;
  filename: string;
  mime: string | null;
  size_bytes: number | null;
  created_at: string;
  kind: "word" | "cell" | "slide" | "pdf" | "other";
  message_id: number | null;
  chat_topic_id: number | null;
  chat_topic_name: string | null;
  chat_topic_kind: string | null;
  uploaded_by_user_id: string | null;
  uploaded_by_name: string | null;
  uploaded_by_avatar: string | null;
  drive_file_id: number | null;
  drive_folder_id: number | null;
  drive_folder_name: string | null;
  drive_topic_id: number | null;
  drive_topic_name: string | null;
  pinned_at: string | null;
  globally_pinned_at: string | null;
}

interface ListResponse {
  items: DocItem[];
  total: number;
}

interface FilterOptions {
  departments: string[];
  chats: { id: number; name: string; kind: string }[];
  users: { id: string; display_name: string; avatar_filename: string | null; departments: string | null }[];
}

const DEPT_LABELS: Record<string, string> = {
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  admin: "Администрация",
  finance: "Финансы",
  supply: "Снабжение",
};

const DATE_PRESETS: { key: string; label: string; days?: number }[] = [
  { key: "any", label: "За всё время" },
  { key: "today", label: "Сегодня", days: 0 },
  { key: "week", label: "За неделю", days: 7 },
  { key: "month", label: "За месяц", days: 30 },
  { key: "quarter", label: "За 3 месяца", days: 90 },
];

interface Filters {
  q: string;
  department: string;
  chat_topic_id: number | null;
  uploaded_by: string;
  date_preset: string;
  sort: "recent" | "edited";
  pinned_only: boolean;
}

const EMPTY_FILTERS: Filters = {
  q: "",
  department: "",
  chat_topic_id: null,
  uploaded_by: "",
  date_preset: "any",
  sort: "recent",
  pinned_only: false,
};

function isoDateNDaysAgo(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 19).replace("T", " ");
}

async function fetchDocsList(f: Filters, org?: number | null): Promise<ListResponse> {
  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const params = new URLSearchParams({ limit: "200" });
  if (org) params.set("org", String(org));   // Редактор организации (04.10.2026)
  if (f.q.trim()) params.set("q", f.q.trim());
  if (f.department) params.set("department", f.department);
  if (f.chat_topic_id) params.set("chat_topic_id", String(f.chat_topic_id));
  if (f.uploaded_by) params.set("uploaded_by", f.uploaded_by);
  if (f.sort) params.set("sort", f.sort);
  if (f.pinned_only) params.set("pinned_only", "true");
  const preset = DATE_PRESETS.find(p => p.key === f.date_preset);
  if (preset && typeof preset.days === "number") {
    params.set("date_from", isoDateNDaysAgo(preset.days));
  }
  const res = await fetch(`${apiBase}/docs/list?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function fetchFilterOptions(): Promise<FilterOptions> {
  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const res = await fetch(`${apiBase}/docs/filter-options`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function fmtSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
    return d.toLocaleString("ru-RU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

/** Пастельный фон thumb-а карточки (tg-style: light tinted, без ярких заливок). */
function kindBg(kind: DocItem["kind"]): string {
  if (kind === "word") return "#e3f2fd";   // light blue
  if (kind === "cell") return "#e8f5e9";   // light green
  if (kind === "slide") return "#fff3e0";  // light orange
  if (kind === "pdf") return "#ffebee";    // light red
  return "#eef0f1";                         // neutral
}

/** Контрастный цвет ext-бейджа поверх пастельного фона. */
function kindFg(kind: DocItem["kind"]): string {
  if (kind === "word") return "#1976d2";
  if (kind === "cell") return "#2e7d32";
  if (kind === "slide") return "#ef6c00";
  if (kind === "pdf") return "#c62828";
  return "#5f6368";
}

function kindLabel(filename: string): string {
  return filename.split(".").pop()?.toUpperCase() || "";
}

interface DropdownProps<T> {
  label: string;
  value: T | null;
  options: { value: T; label: string; sub?: string; left?: React.ReactNode }[];
  onChange: (v: T | null) => void;
  searchable?: boolean;
}

function FilterDropdown<T extends string | number>({
  label, value, options, onChange, searchable,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q) || (o.sub ?? "").toLowerCase().includes(q));
  }, [search, options]);

  const selected = options.find(o => o.value === value);

  return (
    <div className="docs-filter-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`docs-filter-chip${value !== null && value !== "" ? " is-active" : ""}`}
        onClick={() => setOpen(v => !v)}
      >
        <span className="docs-filter-chip-label">{label}{selected ? `: ${selected.label}` : ""}</span>
        {value !== null && value !== "" ? (
          <span
            className="docs-filter-chip-x"
            onClick={(e) => { e.stopPropagation(); onChange(null); setOpen(false); }}
            role="button"
            aria-label="Сбросить"
          >
            <Icon name="x" size={12}/>
          </span>
        ) : (
          <Icon name="chevron_d" size={12}/>
        )}
      </button>
      {open && (
        <div className="docs-filter-pop">
          {searchable && (
            <div className="docs-filter-pop-search">
              <Icon name="search" size={14}/>
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск…"
              />
            </div>
          )}
          <div className="docs-filter-pop-list">
            {filtered.length === 0 && (
              <div className="docs-filter-pop-empty">Ничего не найдено</div>
            )}
            {filtered.map(o => (
              <button
                key={String(o.value)}
                type="button"
                className={`docs-filter-pop-item${o.value === value ? " is-active" : ""}`}
                onClick={() => { onChange(o.value); setOpen(false); setSearch(""); }}
              >
                {o.left && <span className="docs-filter-pop-left">{o.left}</span>}
                <span className="docs-filter-pop-text">
                  <span>{o.label}</span>
                  {o.sub && <span className="docs-filter-pop-sub">{o.sub}</span>}
                </span>
                {o.value === value && <Icon name="check" size={14}/>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function DocsView({ orgId = null, driveTopicId = null }: {
  /** Редактор организации (04.10.2026): только её документы; новые — на её Диск. Без — как было. */
  orgId?: number | null;
  driveTopicId?: number | null;
} = {}) {
  const { user } = useAuth();
  const canPinForAll = !!user && (
    user.is_superuser || user.role === "owner" || user.role === "admin"
  );
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [preview, setPreview] = useState<DocItem | null>(null);
  // Контекстное меню по правой кнопке на карточке документа
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; item: DocItem } | null>(null);
  const [renameItem, setRenameItem] = useState<DocItem | null>(null);
  // Открытый модал «Сохранить на Диск» — если выбран
  const [saveToDriveItem, setSaveToDriveItem] = useState<DocItem | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Список топиков (нужен SaveToDriveModal для выбора диска)
  const topicsQ = useQuery({
    queryKey: ["chat/topics"],
    queryFn: fetchChatTopics,
    enabled: !!ctxMenu || !!saveToDriveItem,
    staleTime: 60_000,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 200);
    return () => clearTimeout(t);
  }, [filters.q]);

  const effectiveFilters = useMemo(() => ({ ...filters, q: debouncedQ }), [filters, debouncedQ]);

  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ["docs/list", effectiveFilters, orgId],
    queryFn: () => fetchDocsList(effectiveFilters, orgId),
    staleTime: 30_000,
  });
  const pinMut = useMutation({
    mutationFn: ({ id, isPinned }: { id: number; isPinned: boolean }) =>
      isPinned ? unpinDoc(id) : pinDoc(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["docs/list"] }),
  });
  const pinAllMut = useMutation({
    mutationFn: ({ id, isPinned }: { id: number; isPinned: boolean }) =>
      isPinned ? unpinDocForAll(id) : pinDocForAll(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["docs/list"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (aid: number) => deleteDoc(aid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["docs/list"] }),
    onError: (e: Error) => alert(`Не удалось удалить: ${e.message}`),
  });
  const optsQ = useQuery({
    queryKey: ["docs/filter-options"],
    queryFn: fetchFilterOptions,
    staleTime: 5 * 60_000,
  });

  const items = listQ.data?.items ?? [];

  const grouped = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const yesterday = today - 24 * 3600_000;
    const weekAgo = today - 7 * 24 * 3600_000;
    const monthAgo = today - 30 * 24 * 3600_000;
    // Закреплённые — ОТДЕЛЬНАЯ группа в начале списка (поверх «Сегодня»).
    // Документ попадает либо в pinned, либо в одну из date-групп — НЕ дублируем.
    const pinned: DocItem[] = [];
    const groups: { label: string; items: DocItem[] }[] = [
      { label: "Сегодня", items: [] },
      { label: "Вчера", items: [] },
      { label: "На этой неделе", items: [] },
      { label: "В этом месяце", items: [] },
      { label: "Раньше", items: [] },
    ];
    for (const it of items) {
      if (it.pinned_at) {
        pinned.push(it);
        continue;
      }
      let ts = 0;
      try {
        const d = new Date(it.created_at.includes("T") ? it.created_at : it.created_at.replace(" ", "T") + "Z");
        ts = d.getTime();
      } catch { ts = 0; }
      if (ts >= today) groups[0].items.push(it);
      else if (ts >= yesterday) groups[1].items.push(it);
      else if (ts >= weekAgo) groups[2].items.push(it);
      else if (ts >= monthAgo) groups[3].items.push(it);
      else groups[4].items.push(it);
    }
    const result: { label: string; items: DocItem[] }[] = [];
    if (pinned.length > 0) result.push({ label: "📌 Закреплённые", items: pinned });
    return [...result, ...groups.filter(g => g.items.length > 0)];
  }, [items]);

  const downloadUrl = (attId: number) =>
    `${(import.meta.env.VITE_API_BASE as string | undefined) ?? "/api"}/chat/attachments/${attId}/download`;

  const opts = optsQ.data;
  const deptOptions = (opts?.departments ?? []).map(d => ({ value: d, label: DEPT_LABELS[d] ?? d }));
  const chatOptions = (opts?.chats ?? []).map(c => ({ value: c.id, label: c.name || "(без названия)" }));
  const userOptions = (opts?.users ?? []).map(u => ({
    value: u.id,
    label: u.display_name || "(без имени)",
    left: <Avatar name={u.display_name || "?"} userId={u.id} avatarFilename={u.avatar_filename ?? null} size={20}/>,
  }));

  const hasActive = filters.department || filters.chat_topic_id || filters.uploaded_by || filters.date_preset !== "any" || filters.sort !== "recent";

  return (
    <div className="docs-view">
      <div className="docs-header">
        <div className="docs-header-title">
          <Icon name="file" size={20}/>
          <span>Редактор документов</span>
        </div>
        <Link
          to="/pages"
          title="Страницы — новый лёгкий редактор (прототип)"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 10px", borderRadius: 8, textDecoration: "none",
            background: "#e8f5e9", color: "#2e7d32", fontSize: 13, fontWeight: 600,
            whiteSpace: "nowrap", flexShrink: 0,
          }}
        >
          <Icon name="edit" size={14}/> Страницы
        </Link>
        <div className="docs-header-search">
          <Icon name="search" size={16}/>
          <input
            type="search"
            placeholder="Поиск по документам…"
            value={filters.q}
            onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
          />
        </div>
        <button
          type="button"
          onClick={() => setSettingsOpen(true)}
          title="Настройки редактора"
          aria-label="Настройки"
          style={{
            background: "transparent", border: "1px solid var(--line)",
            borderRadius: 8, padding: "6px 10px", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 4,
            color: "var(--ink-2)", fontFamily: "inherit",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" ><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg>
        </button>
        <DocsCreateButton onCreated={() => listQ.refetch()} driveTopicId={driveTopicId}/>
      </div>

      <div className="docs-filter-bar">
        <button
          type="button"
          className={`docs-filter-tab${filters.sort === "recent" ? " is-active" : ""}`}
          onClick={() => setFilters(f => ({ ...f, sort: "recent" }))}
        >
          Все
        </button>
        <button
          type="button"
          className={`docs-filter-tab${filters.sort === "edited" ? " is-active" : ""}`}
          onClick={() => setFilters(f => ({ ...f, sort: "edited" }))}
          title="Документы, отредактированные через OnlyOffice (save-as-copy)"
        >
          Недавно отредактированные
        </button>
        <div className="docs-filter-divider"/>
        <FilterDropdown
          label="Отдел"
          value={filters.department || null}
          options={deptOptions}
          onChange={(v) => setFilters(f => ({ ...f, department: v ?? "" }))}
        />
        <FilterDropdown
          label="Чат"
          value={filters.chat_topic_id}
          options={chatOptions}
          onChange={(v) => setFilters(f => ({ ...f, chat_topic_id: v }))}
          searchable
        />
        <FilterDropdown
          label="Автор"
          value={filters.uploaded_by || null}
          options={userOptions}
          onChange={(v) => setFilters(f => ({ ...f, uploaded_by: v ?? "" }))}
          searchable
        />
        <FilterDropdown
          label="Дата"
          value={filters.date_preset === "any" ? null : filters.date_preset}
          options={DATE_PRESETS.filter(p => p.key !== "any").map(p => ({ value: p.key, label: p.label }))}
          onChange={(v) => setFilters(f => ({ ...f, date_preset: v ?? "any" }))}
        />
        {hasActive && (
          <button
            type="button"
            className="docs-filter-reset"
            onClick={() => setFilters({ ...EMPTY_FILTERS, q: filters.q })}
            title="Сбросить все фильтры"
          >
            Сбросить
          </button>
        )}
        <div className="docs-filter-spacer"/>
        <div className="docs-filter-count">
          {listQ.isLoading ? "…" : `${items.length} ${items.length === 1 ? "документ" : items.length < 5 ? "документа" : "документов"}`}
        </div>
      </div>

      <div className="docs-body">
        {listQ.isLoading && <div className="docs-loading">Загрузка…</div>}
        {listQ.error && <div className="docs-error">Ошибка: {(listQ.error as Error).message}</div>}
        {!listQ.isLoading && items.length === 0 && (
          <div className="docs-empty">
            <Icon name="file" size={48}/>
            <div className="docs-empty-title">
              {hasActive || debouncedQ ? "По вашим фильтрам ничего не нашлось" : "Документов нет"}
            </div>
            <div className="docs-empty-sub">
              {hasActive || debouncedQ
                ? "Попробуйте сбросить фильтры или изменить запрос."
                : "Сюда попадают все .docx, .xlsx, .pptx и .pdf файлы из чатов."}
            </div>
          </div>
        )}

        {grouped.map(group => (
          <div key={group.label} className="docs-group">
            <div className="docs-group-label">{group.label}</div>
            <div className="docs-grid">
              {group.items.map(it => {
                const bg = kindBg(it.kind);
                const fg = kindFg(it.kind);
                const ext = kindLabel(it.filename);
                const sourceText = it.drive_folder_name
                  ? `${it.drive_topic_name ?? ""} › ${it.drive_folder_name}`
                  : it.chat_topic_name ?? "";
                const sourceIcon: "folder" | "msg" | null = it.drive_folder_name
                  ? "folder"
                  : it.chat_topic_name ? "msg" : null;
                return (
                  <DocCard
                    key={it.attachment_id}
                    item={it}
                    onOpen={() => setPreview(it)}
                    onOpenCtxMenu={(x, y) => setCtxMenu({ x, y, item: it })}
                  >
                    <div className="docs-card-thumb docs-card-thumb-preview" style={{ background: bg, color: fg, position: "relative" }}>
                      {/* Превью первой страницы документа (Яндекс.Диск-стиль).
                          Backend: /preview-image — PDF/Office → JPEG via pdftoppm.
                          На onError скрываем img → виден ext-бейдж под ним. */}
                      <img
                        src={`/api/chat/attachments/${it.attachment_id}/preview-image`}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="docs-card-thumb-img"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                      <span className="docs-card-thumb-ext">{ext}</span>
                      {it.pinned_at && (
                        <span
                          title="Открепить"
                          role="button"
                          aria-label="Открепить"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            pinMut.mutate({ id: it.attachment_id, isPinned: true });
                          }}
                          style={{
                            position: "absolute", top: 6, right: 6,
                            background: "rgba(255,255,255,0.95)",
                            color: "var(--ink-2, #5f6368)",
                            borderRadius: 999, padding: 4,
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
                            cursor: "pointer",
                          }}
                        ><Icon name="pin" size={12}/></span>
                      )}
                    </div>
                    <div className="docs-card-name">{it.filename}</div>
                    <div className="docs-card-meta">
                      <span>{fmtSize(it.size_bytes)}</span>
                      <span>·</span>
                      <span>{fmtDate(it.created_at)}</span>
                    </div>
                    {it.uploaded_by_name && (
                      <div className="docs-card-author">
                        <Avatar
                          name={it.uploaded_by_name}
                          userId={it.uploaded_by_user_id ?? undefined}
                          avatarFilename={it.uploaded_by_avatar ?? null}
                          size={18}
                        />
                        <span>{it.uploaded_by_name}</span>
                      </div>
                    )}
                    {sourceText && (
                      <div className="docs-card-source" title={sourceText}>
                        {sourceIcon && <Icon name={sourceIcon} size={12}/>}
                        <span>{sourceText}</span>
                      </div>
                    )}
                  </DocCard>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <FilePreviewModal
          url={downloadUrl(preview.attachment_id)}
          filename={preview.filename}
          mime={preview.mime}
          sizeBytes={preview.size_bytes}
          attachmentId={preview.attachment_id}
          onClose={() => setPreview(null)}
          onDelete={preview.drive_file_id ? () => {
            deleteMut.mutate(preview.attachment_id);
            setPreview(null);
          } : undefined}
        />
      )}

      {ctxMenu && (
        <DocsCtxMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          item={ctxMenu.item}
          canPinForAll={canPinForAll}
          onClose={() => setCtxMenu(null)}
          onSaveToDrive={() => {
            setSaveToDriveItem(ctxMenu.item);
            setCtxMenu(null);
          }}
          onTogglePin={() => {
            pinMut.mutate({ id: ctxMenu.item.attachment_id, isPinned: !!ctxMenu.item.pinned_at });
            setCtxMenu(null);
          }}
          onTogglePinAll={() => {
            pinAllMut.mutate({ id: ctxMenu.item.attachment_id, isPinned: !!ctxMenu.item.globally_pinned_at });
            setCtxMenu(null);
          }}
          onRename={() => {
            setRenameItem(ctxMenu.item);
            setCtxMenu(null);
          }}
          onDelete={() => {
            const it = ctxMenu.item;
            setCtxMenu(null);
            if (!it.drive_file_id) {
              alert("Документ не на Диске — нечего удалять.");
              return;
            }
            if (confirm(`Удалить «${it.filename}»? Документ переедет в корзину Диска и будет храниться 30 дней.`)) {
              deleteMut.mutate(it.attachment_id);
            }
          }}
        />
      )}

      {saveToDriveItem && (
        <SaveToDriveModal
          topics={topicsQ.data ?? []}
          items={[{ attachmentId: saveToDriveItem.attachment_id, filename: saveToDriveItem.filename }]}
          onClose={() => setSaveToDriveItem(null)}
          onSaved={() => setSaveToDriveItem(null)}
        />
      )}

      {renameItem && (
        <DocRenameModal
          attachmentId={renameItem.attachment_id}
          currentFilename={renameItem.filename}
          onClose={() => setRenameItem(null)}
          onDone={() => {
            setRenameItem(null);
            qc.invalidateQueries({ queryKey: ["docs/list"] });
          }}
        />
      )}

      {settingsOpen && (
        <DocsSettingsModal onClose={() => setSettingsOpen(false)}/>
      )}
    </div>
  );
}

function DocsSettingsModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const permQ = useQuery({
    queryKey: ["docs/delete-permission"],
    queryFn: fetchDeletePermission,
    staleTime: 0,
  });
  const setPermMut = useMutation({
    mutationFn: (vars: { value: DeletePermission; user_ids?: string[] }) =>
      setDeletePermission(vars.value, vars.user_ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["docs/delete-permission"] }),
    onError: (e: Error) => alert(`Не удалось сохранить: ${e.message}`),
  });
  const current = permQ.data?.value ?? "moderators";
  const currentIds = permQ.data?.user_ids ?? [];

  const opts: { value: DeletePermission; label: string; hint: string }[] = [
    { value: "all", label: "Все участники", hint: "Любой, у кого есть доступ к диску" },
    { value: "moderators", label: "Только модераторы", hint: "Модераторы, создатель топика, автор файла, админы (по умолчанию)" },
    { value: "admins", label: "Только админы", hint: "Только суперпользователи системы" },
    { value: "specific", label: "Выбранные сотрудники", hint: "Перечислить точечно — никто кроме них не сможет удалять" },
  ];
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-window"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(600px, 92vw)", maxHeight: "85vh", display: "flex", flexDirection: "column" }}
      >
        <div className="modal-header">
          <div style={{ fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700 }}>
            ⚙ Настройки редактора
          </div>
          <button className="modal-close" onClick={onClose} title="Закрыть">
            <Icon name="x" size={18}/>
          </button>
        </div>
        <div style={{ padding: "8px 18px 16px", overflowY: "auto" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, marginTop: 8 }}>
            Кто может удалять документы
          </div>
          <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 12 }}>
            Применяется и в редакторе документов, и в Дисках при удалении файлов.
          </div>
          {permQ.isLoading && <div style={{ color: "var(--mute)" }}>Загрузка…</div>}
          {!permQ.isLoading && opts.map(o => (
            <label
              key={o.value}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "10px 8px", border: "1px solid var(--line)",
                borderRadius: 8, marginBottom: 6,
                cursor: setPermMut.isPending ? "not-allowed" : "pointer",
                background: current === o.value ? "rgba(79,174,78,0.07)" : "transparent",
                borderColor: current === o.value ? "var(--tg-accent, #4fae4e)" : "var(--line)",
              }}
            >
              <input
                type="radio"
                name="delete-perm"
                value={o.value}
                checked={current === o.value}
                disabled={setPermMut.isPending}
                onChange={() => setPermMut.mutate({ value: o.value, user_ids: o.value === "specific" ? currentIds : undefined })}
                style={{ marginTop: 2 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{o.label}</div>
                <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 2 }}>{o.hint}</div>
              </div>
            </label>
          ))}

          {current === "specific" && (
            <UserMultiPicker
              selectedIds={currentIds}
              onChange={(ids) => setPermMut.mutate({ value: "specific", user_ids: ids })}
              disabled={setPermMut.isPending}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function UserMultiPicker({ selectedIds, onChange, disabled }: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const usersQ = useQuery({
    queryKey: ["profiles/active"],
    queryFn: fetchActiveUsers,
    staleTime: 60_000,
  });
  const [search, setSearch] = useState("");
  const users = usersQ.data ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => (u.display_name ?? "").toLowerCase().includes(q));
  }, [users, search]);
  const selSet = new Set(selectedIds);

  function toggle(id: string) {
    if (disabled) return;
    const next = new Set(selSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  }
  return (
    <div style={{ marginTop: 12, border: "1px solid var(--line)", borderRadius: 8, padding: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        Сотрудники с правом удалять ({selectedIds.length})
      </div>
      <input
        type="search"
        placeholder="Поиск по имени…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          width: "100%", padding: "6px 10px", border: "1px solid var(--line)",
          borderRadius: 6, marginBottom: 8, fontSize: 13,
          fontFamily: "inherit", outline: "none",
        }}
      />
      <div style={{ maxHeight: 240, overflowY: "auto" }}>
        {usersQ.isLoading && <div style={{ color: "var(--mute)", padding: 6 }}>Загрузка…</div>}
        {!usersQ.isLoading && filtered.length === 0 && (
          <div style={{ color: "var(--mute)", padding: 6, fontSize: 12 }}>Никого не найдено</div>
        )}
        {filtered.map(u => {
          const checked = selSet.has(u.id);
          return (
            <label
              key={u.id}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 8px", cursor: disabled ? "not-allowed" : "pointer",
                borderRadius: 6,
                background: checked ? "rgba(79,174,78,0.08)" : "transparent",
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggle(u.id)}
              />
              <Avatar
                name={u.display_name ?? "Пользователь"}
                userId={u.id}
                avatarFilename={u.avatar_filename ?? null}
                size={26}
              />
              <span style={{ fontSize: 13 }}>{u.display_name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/**
 * DocCard — обёртка над `<button.docs-card>` с long-press на тач-устройствах.
 * Без long-press на мобиле невозможно открыть контекстное меню (нет правого клика),
 * и pinned-документ нельзя было открепить.
 *
 *   - tap / click — onOpen (превью)
 *   - long-press 500мс — onOpenCtxMenu (меню действий)
 *   - правый клик (desktop) — onOpenCtxMenu
 *   - движение пальцем — отмена long-press (юзер скроллит)
 */
function DocCard({ item, onOpen, onOpenCtxMenu, children }: {
  item: DocItem;
  onOpen: () => void;
  onOpenCtxMenu: (x: number, y: number) => void;
  children: React.ReactNode;
}) {
  const timer = useRef<number | null>(null);
  const startedAt = useRef<{ x: number; y: number } | null>(null);
  const firedLong = useRef(false);

  function clearTimer() {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }
  return (
    <button
      type="button"
      className={`docs-card${item.pinned_at ? " is-pinned" : ""}`}
      title={item.filename}
      onClick={(e) => {
        // long-press уже сработал → не открываем превью
        if (firedLong.current) {
          firedLong.current = false;
          e.preventDefault();
          return;
        }
        onOpen();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpenCtxMenu(e.clientX, e.clientY);
      }}
      onTouchStart={(e) => {
        const t = e.touches[0];
        if (!t) return;
        startedAt.current = { x: t.clientX, y: t.clientY };
        firedLong.current = false;
        clearTimer();
        timer.current = window.setTimeout(() => {
          firedLong.current = true;
          if ("vibrate" in navigator) try { navigator.vibrate?.(20); } catch { /* ignore */ }
          onOpenCtxMenu(t.clientX, t.clientY);
        }, 500);
      }}
      onTouchMove={(e) => {
        // отмена long-press если палец сдвинулся >8px (юзер скроллит)
        const s = startedAt.current;
        const t = e.touches[0];
        if (!s || !t) return;
        if (Math.abs(t.clientX - s.x) > 8 || Math.abs(t.clientY - s.y) > 8) {
          clearTimer();
        }
      }}
      onTouchEnd={clearTimer}
      onTouchCancel={clearTimer}
    >
      {children}
    </button>
  );
}


function DocsCtxMenu({ x, y, item, canPinForAll, onClose, onSaveToDrive, onTogglePin, onTogglePinAll, onRename, onDelete }: {
  x: number; y: number;
  item: DocItem;
  canPinForAll: boolean;
  onClose: () => void;
  onSaveToDrive: () => void;
  onTogglePin: () => void;
  onTogglePinAll: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);
  const alreadyOnDrive = !!item.drive_file_id;
  // Сдвигаем меню если не помещается у правого/нижнего края экрана
  const MENU_W = 280;
  const MENU_H = alreadyOnDrive ? 180 : 120;
  const left = Math.min(x, window.innerWidth - MENU_W - 8);
  const top = Math.min(y, window.innerHeight - MENU_H - 8);
  // Текущее расположение: «<диск> / <папка>»
  const currentLocation = alreadyOnDrive
    ? [item.drive_topic_name, item.drive_folder_name].filter(Boolean).join(" / ")
    : "";
  return (
    <div
      ref={ref}
      style={{
        position: "fixed", left, top, zIndex: 9999,
        minWidth: MENU_W,
        background: "#fff", border: "1px solid var(--line)",
        borderRadius: 8, padding: 4,
        boxShadow: "0 6px 24px rgba(0,0,0,0.14)",
      }}
    >
      {alreadyOnDrive && (
        <div style={{
          padding: "6px 10px 8px",
          fontSize: 11.5, color: "var(--mute)",
          borderBottom: "1px solid var(--line)",
          marginBottom: 4,
        }}>
          <div style={{ marginBottom: 2 }}>На Диске:</div>
          <div style={{
            fontSize: 12.5, color: "var(--ink)", fontWeight: 500,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <Icon name="folder" size={14}/>
            <span style={{
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{currentLocation || "Диск"}</span>
          </div>
        </div>
      )}
      <DocsCtxItem
        icon={<Icon name="folder" size={16}/>}
        label={alreadyOnDrive ? "Перенести в другую папку" : "Перенести на Диск"}
        onClick={onSaveToDrive}
      />
      <DocsCtxItem
        icon={<span style={{ fontSize: 14, lineHeight: 1 }}>{item.pinned_at ? "📍" : "📌"}</span>}
        label={item.pinned_at ? "Открепить" : "Закрепить"}
        onClick={onTogglePin}
      />
      {canPinForAll && (
        <DocsCtxItem
          icon={<span style={{ fontSize: 14, lineHeight: 1 }}>{item.globally_pinned_at ? "📍" : "📌"}</span>}
          label={item.globally_pinned_at ? "Открепить у всех" : "Закрепить у всех"}
          onClick={onTogglePinAll}
        />
      )}
      <DocsCtxItem
        icon={<Icon name="edit" size={16}/>}
        label="Переименовать"
        onClick={onRename}
      />
      {alreadyOnDrive && (
        <>
          <div style={{ height: 1, background: "var(--line)", margin: "4px 0" }}/>
          <DocsCtxItem
            icon={<span style={{ fontSize: 14, lineHeight: 1 }}>🗑</span>}
            label="Удалить (в корзину)"
            onClick={onDelete}
            danger
          />
        </>
      )}
    </div>
  );
}

function DocsCtxItem({ icon, label, onClick, danger }: {
  icon: React.ReactNode; label: string;
  onClick: () => void; danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        width: "100%", padding: "8px 10px", border: "none",
        background: "transparent", textAlign: "left", fontSize: 13,
        fontFamily: "inherit", cursor: "pointer",
        color: danger ? "#d92d20" : "var(--ink)", borderRadius: 6,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = danger ? "rgba(217,45,32,0.07)" : "rgba(0,0,0,0.05)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}


function DocsCreateButton({ onCreated, driveTopicId = null }: { onCreated: () => void; driveTopicId?: number | null }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nameModal, setNameModal] = useState<{ kind: "word" | "cell" | "slide"; defaultName: string } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  // «Создать» = новый документ Word в SuperDoc (/pages/word).
  async function createPageAndOpen(kind: "word" | "cell" = "word") {
    if (busy) return;
    setOpen(false);
    setBusy(true);
    try {
      const { createOfficeDoc } = await import("@/api/pages");
      const isCell = kind === "cell";
      const d = await createOfficeDoc(kind, isCell ? "Таблица" : "Документ");
      onCreated();
      if (isCell) void navigate({ to: "/pages/sheet/$attId", params: { attId: String(d.attachment_id) } });
      else void navigate({ to: "/pages/word/$attId", params: { attId: String(d.attachment_id) } });
    } catch (e) {
      alert(`Не удалось создать страницу: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && wrapRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);


  async function create(kind: "word" | "cell" | "slide", name: string) {
    if (busy) return;
    setBusy(true);
    try {
      const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
      const res = await fetch(`${base}/onlyoffice/create`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(driveTopicId ? { kind, name, drive_topic_id: driveTopicId } : { kind, name }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      window.open(`${base}/onlyoffice/page/${data.attachment_id}`, `oo_${data.attachment_id}`);
      onCreated();
      setNameModal(null);
    } catch (e) {
      alert(`Не удалось создать: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={wrapRef} className="docs-create-wrap" style={{ position: "relative" }}>
      <button
        type="button"
        className="btn btn-sm btn-primary docs-create-btn"
        onClick={() => setOpen(v => !v)}
        disabled={busy}
        title="Создать документ Word или таблицу Excel"
      >
        <Icon name="plus" size={14}/> {busy ? "Создаю…" : "Создать"}
      </button>
      {open && (
        <div className="docs-create-menu">
          <button type="button" onClick={() => void createPageAndOpen("word")}>
            <span className="docs-create-icon"><Icon name="report" size={18}/></span>
            <span><b>Документ Word</b><small>.docx — откроется в редакторе</small></span>
          </button>
          <button type="button" onClick={() => void createPageAndOpen("cell")}>
            <span className="docs-create-icon"><Icon name="chart" size={18}/></span>
            <span><b>Таблица Excel</b><small>.xlsx — откроется в редакторе</small></span>
          </button>
        </div>
      )}
      {nameModal && (
        <NewDocNameModal
          kind={nameModal.kind}
          defaultName={nameModal.defaultName}
          busy={busy}
          onSubmit={(name) => create(nameModal.kind, name)}
          onClose={() => setNameModal(null)}
        />
      )}
    </div>
  );
}


function NewDocNameModal({ kind, defaultName, busy, onSubmit, onClose }: {
  kind: "word" | "cell" | "slide";
  defaultName: string;
  busy: boolean;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const extLabel = { word: ".docx", cell: ".xlsx", slide: ".pptx" }[kind];
  const title = { word: "Новый документ", cell: "Новая таблица", slide: "Новая презентация" }[kind];
  const kindEmoji = { word: "📄", cell: "📊", slide: "📽" }[kind];
  return (
    <div className="kb-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="kb-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="kb-modal-title">
          <span className="kb-modal-title-emoji" aria-hidden>{kindEmoji}</span>
          {title}
        </div>
        <div className="kb-modal-field">
          <label className="kb-modal-label">Название</label>
          <div className="kb-modal-input-wrap">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim()) onSubmit(name.trim());
                if (e.key === "Escape") onClose();
              }}
              placeholder="Например: Договор с ООО «Ромашка»"
              className="kb-modal-input"
            />
            <span className="kb-modal-input-suffix">{extLabel}</span>
          </div>
          <div className="kb-modal-hint">Расширение {extLabel} добавится автоматически.</div>
        </div>
        <div className="kb-modal-actions">
          <button type="button" className="kb-btn kb-btn-ghost" onClick={onClose} disabled={busy}>Отмена</button>
          <button
            type="button"
            className="kb-btn kb-btn-primary"
            disabled={busy || !name.trim()}
            onClick={() => onSubmit(name.trim())}
          >
            {busy ? "Создаю…" : "Создать и открыть"}
          </button>
        </div>
      </div>
    </div>
  );
}


function DocRenameModal({ attachmentId, currentFilename, onClose, onDone }: {
  attachmentId: number;
  currentFilename: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const dotIdx = currentFilename.lastIndexOf(".");
  const baseInit = dotIdx > 0 ? currentFilename.slice(0, dotIdx) : currentFilename;
  const ext = dotIdx > 0 ? currentFilename.slice(dotIdx) : "";
  const [name, setName] = useState(baseInit);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    const n = name.trim();
    if (!n || n === baseInit) return;
    setBusy(true);
    setErr(null);
    try {
      await renameAttachment(attachmentId, n);
      onDone();
    } catch (e) {
      setErr((e as Error).message || "Не удалось переименовать");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="kb-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="kb-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="kb-modal-title">
          <span className="kb-modal-title-emoji" aria-hidden>✏️</span>
          Переименовать
        </div>
        <div className="kb-modal-field">
          <label className="kb-modal-label">Новое имя файла</label>
          <div className="kb-modal-input-wrap">
            <input
              autoFocus
              value={name}
              onChange={(e) => { setName(e.target.value); setErr(null); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim()) submit();
                if (e.key === "Escape") onClose();
              }}
              placeholder="Введите новое имя"
              className="kb-modal-input"
            />
            {ext && <span className="kb-modal-input-suffix">{ext}</span>}
          </div>
          <div className="kb-modal-hint">Расширение сохраняется автоматически.</div>
        </div>
        {err && <div className="kb-modal-err">{err}</div>}
        <div className="kb-modal-actions">
          <button type="button" className="kb-btn kb-btn-ghost" onClick={onClose} disabled={busy}>Отмена</button>
          <button
            type="button"
            className="kb-btn kb-btn-primary"
            disabled={busy || !name.trim() || name.trim() === baseInit}
            onClick={submit}
          >
            {busy ? "Сохраняю…" : "Переименовать"}
          </button>
        </div>
      </div>
    </div>
  );
}
